<template>
  <!-- class="add-employee-dialog" -->
  <global-dialog-card
    v-model="model"
    :max-width="700"
    :title="formTitle"
    :subtitle="employee?.id ? `ID: ${employee.id}` : ''"
  >
    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <employee-avatar-section
      :employee="employee"
      :conf="conf"
      :show-avatar-edit.sync="showAvatarEdit"
      @upload="upload"
    />

    <v-form v-model="valid">
      <employee-form-section
        :employee="employee"
        :parent-companies="parentCompanies"
        :companies="companies"
        :error-message-email="errorMessageEmail"
        :searchs.sync="searchs"
        :selected-company="selectedCompany"
        :is-delivery="isDeliveryRoute"
        @open-change-company="openChangeCompany"
      />
    </v-form>
    <template #actions>
      <v-btn text @click="model = false">
        {{ $t("cancel") }}
      </v-btn>
      <primary-button :dark="valid" @click="save" :loading="loading" :disabled="!valid">
        {{ $t("save") }}
      </primary-button>
    </template>
  </global-dialog-card>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import deliveryUsersContext from "@/mixins/deliveryUsersContext";
import employeeService from "@/services/employee";
import conf from "@/config";

// Import new subcomponents
import EmployeeAvatarSection from "./EmployeeAvatarSection.vue";
import EmployeeFormSection from "./EmployeeFormSection.vue";

export default {
  mixins: [hasModel, hasSnackbar, deliveryUsersContext],
  name: "CompanyEmployeeDialog",
  components: {
    EmployeeAvatarSection,
    EmployeeFormSection,
  },
  props: {
    employee: {
      type: Object,
      default: () => ({
        id: 0,
      }),
    },
    selectedCompany: {
      type: Object,
      required: false,
    },
    companies: {
      type: Array,
      default: () => [],
    },
    parentCompanies: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      conf,
      errorMessageEmail: "",
      searchs: [],
      loading: false,
      showAvatarEdit: false,
      valid: false,
    };
  },
  computed: {
    formTitle() {
      return this.employee.id ? this.$t("edit_user") : this.$t("new_user");
    },
    companyId() {
      return this.$route.params.id || this.$store.state.company?.id;
    },
    isDeliveryRoute() {
      return this.isDeliveryUsersContext;
    },
    isRestaurantRoute() {
      return this.$route.name === "restoran-id-korisnici";
    },
  },
  methods: {
    async save() {
      this.errorMessageEmail = "";

      if (!this.employee.hasOwnProperty("type")) {
        this.errorMessageEmail = this.$t("type_field_required");
        return;
      }

      this.loading = true;

      try {
        if (this.employee.id) {
          // Update existing user
          await this.updateEmployee();
        } else {
          // Create new user
          await this.createEmployee();
        }
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        this.loading = false;
        this.model = false;
      }
    },

    async updateEmployee() {
      try {
        if (this.isDeliveryRoute) {
          // Dedicated delivery endpoint — never touch PATCH /user/:id, which
          // rewires the company relation that does not apply to delivery.
          // Only name / lastname / phone / type are accepted
          // (09_09_2026 backend answer, point 3).
          await employeeService.updateUserDelivery(this.companyId, this.employee.id, {
            name: this.employee.name,
            lastname: this.employee.lastname,
            phone: this.employee.phone,
            type: this.employee.type,
          });
        } else {
          const userData = {
            ...this.employee,
            company_id: this.employee.company?.id || this.employee.company_id,
          };
          await employeeService.updateUser(this.employee.id, userData);
        }
        this.$emit("employeeUpdated");
        this.showSuccessSnackbar(this.$t("user_updated_successfully"));
      } catch (error) {
        console.warn("Error updating employee:", error);
        if (error.response?.data?.status === "failed") {
          this.errorMessageEmail = error.response.data.message;
        }
        throw error;
      }
    },

    async createEmployee() {
      const userData = {
        ...this.employee,
        can_report: this.isDeliveryRoute ? 0 : this.employee.can_report,
      };

      try {
        let response;
        if (this.isRestaurantRoute) {
          response = await employeeService.createRestaurantUser(
            this.companyId,
            userData,
            this.isDeliveryRoute
          );
        } else {
          response = await employeeService.createCompanyUser(
            this.companyId,
            userData,
            this.isDeliveryRoute
          );
        }

        this.$emit("employeeAdded", response.data);
        this.showSuccessSnackbar(this.$t("user_created_successfully"));
      } catch (error) {
        if (error.response?.data?.status === "failed") {
          this.errorMessageEmail = error.response.data.message;
        }
        throw error;
      }
    },

    handleSaveError(error) {
      const message = error.response?.data?.message || "user_create_error_prefix";
      this.showErrorSnackbar(this.$t(message));
    },

    async upload(event) {
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      formData.append("image_path", "users/photos/");

      try {
        const { data } = await employeeService.uploadUserPhoto(formData);
        this.employee.image_path = "users/photos/small/" + data;
        this.showAvatarEdit = false;
        this.showSuccessSnackbar(
          this.$t("photo_uploaded_successfully") || "Fotografija uspešno otpremljena"
        );
      } catch (error) {
        this.showErrorSnackbar(
          this.$t("photo_upload_error") || "Greška pri otpremanju fotografije"
        );
      }
    },

    openChangeCompany() {
      this.$refs.confirmBox
        .open(this.$t("change_user_company"), this.$t("change_company_confirmation"), {
          color: "warning",
        })
        .then(() => {
          this.changeCompany();
        })
        .catch((err) => console.warn(err));
    },

    async changeCompany() {
      try {
        await employeeService.changeUserCompany(this.employee);
        this.showSuccessSnackbar(
          this.$t("company_changed_successfully") || "Kompanija uspešno promenjena"
        );
        this.$emit("companyUpdated");
        this.dialog = false;
      } catch (error) {
        this.showErrorSnackbar(
          this.$t("company_change_error") || "Greška pri promeni kompanije"
        );
      }
    },
  },
};
</script>
